/**
 * Everything one open book needs, in one hook.
 *
 * The design decision that shapes this file: the whole sync payload is fetched
 * once, up front. A twenty hour book is a few hundred kilobytes of timings, and
 * having them all in memory is what makes clicking a search result, or jumping to
 * an arbitrary page, instant. Chapter TEXT stays lazy, because that is the part
 * that is genuinely large.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { t } from "../i18n";
import { api } from "./api";
import {
  loadAudioPrefs,
  rateFor,
  saveAudioPrefs,
  subscribe as subscribeAudioPrefs,
  type AudioPrefs,
} from "./audioPrefs";
import { bindMediaSession, setMediaPlaybackState, updateMediaPosition } from "./mediaSession";
import { Player, sentenceAt, type PlayerState } from "./player";
import { fadeVolume, SleepTimer, type SleepPick, type SleepState } from "./sleepTimer";
import type { BookDetail, Chapter, PageMap, Sentence } from "./types";

export type ViewMode = "text" | "page";

const PROGRESS_INTERVAL = 5000;

/** Minutes added by "add more time" in the sleep menu: one step below the 15
 * minute preset, so a snooze never overshoots the shortest deliberate choice. */
const SLEEP_EXTEND_MINUTES = 10;

export interface ReaderState {
  book: BookDetail | null;
  sentences: Sentence[];
  pagemap: PageMap | null;
  chapter: Chapter | null;
  chapterIndex: number;
  loading: boolean;
  error: string | null;
  player: PlayerState;
  /** The sentence the reader is looking at: the audio one, or the last click. */
  focus: number;
  page: number | null;
}

/**
 * The flat shape `AudioPrefsMenu.tsx` (owned by the same lot) expects. Kept
 * local, structurally compatible, rather than imported from `reader/`: `lib/`
 * modules never import from `reader/` in this codebase, only the other way.
 */
export interface ReaderAudioPrefs {
  skipBack: number;
  skipForward: number;
  /** The rate that applies to the book currently open. */
  rate: number;
  boost: boolean;
  normalize: boolean;
  trimSilence: boolean;
}

/** Which PDF page a character offset falls on. The map is monotonic, so binary search. */
export function pageAt(map: PageMap | null, chap: number, offset: number): number | null {
  if (!map || map.pages.length === 0) return null;
  let low = 0;
  let high = map.pages.length - 1;
  let found = 0;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const span = map.pages[middle];
    if (span.chap < chap || (span.chap === chap && span.cs <= offset)) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return map.pages[found].page;
}

export function useReader(bookId: string) {
  const playerRef = useRef<Player>(null as unknown as Player);
  if (playerRef.current === null) playerRef.current = new Player();
  const player = playerRef.current;

  const sleepTimerRef = useRef<SleepTimer>(null as unknown as SleepTimer);
  if (sleepTimerRef.current === null) sleepTimerRef.current = new SleepTimer();
  const sleepTimer = sleepTimerRef.current;

  const [book, setBook] = useState<BookDetail | null>(null);
  const [sentences, setSentences] = useState<Sentence[]>([]);
  const [pagemap, setPagemap] = useState<PageMap | null>(null);
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerState, setPlayerState] = useState<PlayerState>(player.snapshot);
  const [sleepState, setSleepState] = useState<SleepState>(sleepTimer.snapshot);
  const [audioPrefsState, setAudioPrefsState] = useState<AudioPrefs>(() => loadAudioPrefs());
  const [clicked, setClicked] = useState(-1);
  const [view, setView] = useState<ViewMode>("text");

  const chapterCache = useRef(new Map<number, Chapter>());

  useEffect(() => player.subscribe(setPlayerState), [player]);
  useEffect(() => () => player.destroy(), [player]);

  useEffect(() => sleepTimer.subscribe(setSleepState), [sleepTimer]);
  useEffect(() => () => sleepTimer.destroy(), [sleepTimer]);
  useEffect(() => subscribeAudioPrefs(setAudioPrefsState), []);

  // Load the book, its timings and its page map. One effect, so a book change
  // never leaves the timings of the previous one in place.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    chapterCache.current.clear();

    (async () => {
      const detail = await api.book(bookId, controller.signal);
      const [sync, map, progress] = await Promise.all([
        detail.has_sync ? api.sync(bookId, controller.signal).catch(() => null) : null,
        detail.has_pagemap ? api.pagemap(bookId, controller.signal).catch(() => null) : null,
        api.progress(bookId, controller.signal).catch(() => null),
      ]);
      if (controller.signal.aborted) return;

      const timings = sync?.sentences ?? [];
      setBook(detail);
      setSentences(timings);
      setPagemap(map);

      // Associate the player with this book before load(), so a mid-flight
      // rate change (or the async prefs it depends on) targets the right id.
      player.setBook(bookId);
      const startAt = progress?.seconds ?? 0;
      if (detail.has_audio)
        player.load(api.audioUrl(bookId, detail.audio_rev), timings, startAt, rateFor(bookId));
      const startIndex = progress?.sentence ?? (timings.length ? sentenceAt(timings, startAt) : -1);
      setClicked(startIndex);
      setChapterIndex(timings[startIndex]?.chap ?? 0);
      setLoading(false);
    })().catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(cause instanceof Error ? cause.message : t("errorBookDetail"));
      setLoading(false);
    });

    return () => controller.abort();
  }, [bookId, player]);

  // The chapter the reader is on follows the audio, unless they navigated away.
  const audioChapter = sentences[playerState.index]?.chap;
  useEffect(() => {
    if (playerState.playing && audioChapter !== undefined) setChapterIndex(audioChapter);
  }, [audioChapter, playerState.playing]);

  useEffect(() => {
    const cached = chapterCache.current.get(chapterIndex);
    if (cached) {
      setChapter(cached);
      return;
    }
    const controller = new AbortController();
    api
      .chapter(bookId, chapterIndex, controller.signal)
      .then((loaded) => {
        chapterCache.current.set(chapterIndex, loaded);
        if (!controller.signal.aborted) setChapter(loaded);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [bookId, chapterIndex]);

  // Save the position regularly, and once more on the way out. Losing your place
  // in a twenty hour book is the one failure a reader never forgives.
  const focus = playerState.index >= 0 ? playerState.index : clicked;
  const focusRef = useRef(focus);
  focusRef.current = focus;
  const timeRef = useRef(playerState.time);
  timeRef.current = playerState.time;

  useEffect(() => {
    if (!book) return;
    const save = () => {
      if (focusRef.current < 0) return;
      void api.saveProgress(bookId, focusRef.current, timeRef.current);
    };
    const timer = window.setInterval(save, PROGRESS_INTERVAL);
    window.addEventListener("pagehide", save);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", save);
      save();
    };
  }, [bookId, book]);

  /**
   * Go to a sentence. `play` is what a tap means: the reader pointed at a line
   * and expects to hear it, not to silently move a cursor they cannot see.
   */
  const goToSentence = useCallback(
    (index: number, options?: { play?: boolean }) => {
      const sentence = sentences[index];
      setClicked(index);
      if (sentence) {
        setChapterIndex(sentence.chap);
        if (book?.has_audio) {
          player.seekToSentence(index);
          if (options?.play) player.play();
        }
      }
    },
    [book, player, sentences],
  );

  const goToChapter = useCallback(
    (index: number) => {
      if (!book) return;
      setChapterIndex(Math.max(0, Math.min(index, book.chapters - 1)));
    },
    [book],
  );

  // Lock screen and headset controls. Rebound whenever the book (and so
  // goToChapter, which depends on it) changes, not on every chapter turn: the
  // ref below keeps previous/next chapter targeting the current chapter
  // without forcing a rebind on every page.
  const chapterIndexRef = useRef(chapterIndex);
  chapterIndexRef.current = chapterIndex;

  useEffect(() => {
    if (!book || !book.has_audio) return;
    const detach = bindMediaSession(
      { title: book.title, author: book.author, cover: book.cover ?? undefined },
      {
        play: () => player.play(),
        pause: () => player.pause(),
        seekBack: () => player.nudge(-player.skipBackSeconds),
        seekForward: () => player.nudge(player.skipForwardSeconds),
        seekTo: (time) => player.seek(time),
        previousChapter: () => goToChapter(chapterIndexRef.current - 1),
        nextChapter: () => goToChapter(chapterIndexRef.current + 1),
      },
    );
    return detach;
  }, [book, player, goToChapter]);

  // Reflects play/pause on the lock screen the moment it changes: this one
  // must not wait for the once-per-second throttle below, or a tap on the
  // headset button would look ignored for up to a second.
  useEffect(() => {
    setMediaPlaybackState(playerState.playing);
  }, [playerState.playing]);

  // Position updates and the sleep timer's clock both only need whole second
  // resolution, so both are gated on the same "a new second started" check
  // instead of running on every animation frame tick.
  const lastWholeSecondRef = useRef(-1);
  useEffect(() => {
    const whole = Math.floor(playerState.time);
    if (whole === lastWholeSecondRef.current) return;
    lastWholeSecondRef.current = whole;

    updateMediaPosition(playerState.duration, playerState.time, playerState.rate);

    const chapterEnd = book?.chapter_marks[chapterIndex]?.end;
    if (chapterEnd !== undefined) sleepTimer.update(playerState.time, chapterEnd, chapterIndex);
  }, [playerState.time, playerState.duration, playerState.rate, book, chapterIndex, sleepTimer]);

  // Fade the last seconds of a countdown. `setFade` is a plain assignment on
  // the audio chain, safe to call every time the remaining count changes.
  useEffect(() => {
    player.setFade(fadeVolume(sleepState.remaining));
  }, [player, sleepState.remaining]);

  // A countdown reaching zero on its own must pause playback; the reader
  // tapping "Off" in the menu must not. Both end up here as the same "mode
  // went from armed to off" transition, so a ref set right before the two
  // deliberate calls (pickSleep, stopSleep) is what tells them apart.
  const manualSleepOff = useRef(false);
  const wasSleepOffRef = useRef(sleepState.mode.kind === "off");
  useEffect(() => {
    const isOff = sleepState.mode.kind === "off";
    const wasOff = wasSleepOffRef.current;
    wasSleepOffRef.current = isOff;
    if (isOff && !wasOff && !manualSleepOff.current) player.pause();
    manualSleepOff.current = false;
  }, [sleepState.mode.kind, player]);

  const pickSleep = useCallback(
    (choice: SleepPick) => {
      manualSleepOff.current = true;
      sleepTimer.pick(choice);
    },
    [sleepTimer],
  );

  const stopSleep = useCallback(() => {
    manualSleepOff.current = true;
    sleepTimer.stop();
  }, [sleepTimer]);

  const extendSleep = useCallback(() => {
    sleepTimer.extend(SLEEP_EXTEND_MINUTES);
  }, [sleepTimer]);

  const audioPrefsValues = useMemo<ReaderAudioPrefs>(
    () => ({
      skipBack: audioPrefsState.skipBack,
      skipForward: audioPrefsState.skipForward,
      rate: playerState.rate,
      boost: audioPrefsState.boost,
      normalize: audioPrefsState.normalize,
      trimSilence: audioPrefsState.trimSilence,
    }),
    [audioPrefsState, playerState.rate],
  );

  const setAudioPrefsPatch = useCallback(
    (patch: Partial<ReaderAudioPrefs>) => {
      const { rate, ...rest } = patch;
      if (rate !== undefined) player.setRate(rate);
      if (Object.keys(rest).length > 0) saveAudioPrefs(rest);
    },
    [player],
  );

  const page = useMemo(() => {
    const sentence = sentences[focus];
    if (sentence?.page) return sentence.page;
    if (!chapter) return null;
    return pageAt(pagemap, chapterIndex, sentence?.cs ?? 0);
  }, [chapter, chapterIndex, focus, pagemap, sentences]);

  return {
    state: {
      book,
      sentences,
      pagemap,
      chapter,
      chapterIndex,
      loading,
      error,
      player: playerState,
      focus,
      page,
    } satisfies ReaderState,
    view,
    setView,
    player,
    goToSentence,
    goToChapter,
    sleep: {
      state: sleepState,
      pick: pickSleep,
      extend: extendSleep,
      stop: stopSleep,
    },
    audioPrefs: {
      values: audioPrefsValues,
      onChange: setAudioPrefsPatch,
    },
  };
}
